import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BOOKING_LINK, CANDIDATE_APPLY_ROUTE } from '../constants';

const CallToAction: React.FC = () => {
    const navigate = useNavigate();
    const [supportsWebP, setSupportsWebP] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        // Check WebP support
        const webP = new Image();
        webP.onload = webP.onerror = function () {
            setSupportsWebP(webP.height === 2);
        };
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';

        // Preload the appropriate image
        const img = new Image();
        img.onload = () => setImageLoaded(true);
        img.src = supportsWebP
            ? '/background/modern_pixel2.webp'
            : '/background/modern_pixel2-optimized.png';
    }, []);

    return (
        <section
            className="relative overflow-x-hidden px-4 py-8 sm:px-16 sm:py-[80px] max-w-full"
            style={{
                boxSizing: 'border-box',
                minHeight: '640px',
                width: '100%',
                borderRadius: '20px',
                margin: '0 auto',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Optimized Pixel Texture Background with progressive loading */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: imageLoaded
                        ? `url(${supportsWebP
                            ? '/background/modern_pixel2.webp'
                            : '/background/modern_pixel2-optimized.png'})`
                        : 'url(/background/modern_pixel2-placeholder.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: isHovered ? 0.9 : 0.7,
                    zIndex: 0,
                    mixBlendMode: 'saturation',
                    borderRadius: '20px',
                    filter: imageLoaded ? 'none' : 'blur(8px)',
                    transition: 'all 0.3s ease-out'
                }}
            />


            <div className="relative text-center mx-auto max-w-full px-4" style={{ zIndex: 10 }}>
                <h2
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 700,
                        fontSize: 'clamp(48px, 8vw, 128px)',
                        lineHeight: 'clamp(60px, 8vw, 140px)',
                        letterSpacing: '-0.05em',
                        color: '#FFFFFF',
                        marginBottom: '24px'
                    }}
                >
                    Ready to rethink recruiting?
                </h2>
                <p
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 200,
                        fontSize: 'clamp(18px, 2vw, 32px)',
                        lineHeight: 'clamp(26px, 2.5vw, 44px)',
                        letterSpacing: '-0.05em',
                        color: '#FFFFFF',
                        marginBottom: '64px',
                        maxWidth: '560px',
                        margin: '0 auto 64px',
                        textAlign: 'center'
                    }}
                >
                    Choose your path and start with Hush today
                </p>
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 justify-center items-center">
                    <div className="flex flex-col items-center gap-3">
                        <button
                            onClick={() => window.open(BOOKING_LINK, '_blank')}
                            className="flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                            style={{
                                width: '100%',
                                maxWidth: 'min(374px, 90vw)',
                                height: '77px',
                                padding: '12px 30px',
                                background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                                boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                                filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                                backdropFilter: 'blur(50px)',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '24px',
                                lineHeight: '20px',
                                color: '#FFFFFF'
                            }}>
                                Request a Demo
                            </span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <span style={{
                            fontSize: '16px',
                            color: '#B1B1B1',
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            lineHeight: '20px'
                        }}>
                            For Employers
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <button
                            onClick={() => navigate(CANDIDATE_APPLY_ROUTE)}
                            className="flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                            style={{
                                width: '100%',
                                maxWidth: 'min(374px, 90vw)',
                                height: '77px',
                                padding: '12px 30px',
                                background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                                boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                                filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                                backdropFilter: 'blur(50px)',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '24px',
                                lineHeight: '20px',
                                color: '#FFFFFF'
                            }}>
                                Get Started Free
                            </span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <span style={{
                            fontSize: '16px',
                            color: '#B1B1B1',
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            lineHeight: '20px'
                        }}>
                            For Candidates
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CallToAction;
